import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LinkedProfilesSwitcher } from './LinkedProfilesSwitcher';
import AmbientNetwork from './AmbientNetwork';
import { useModule, MODULES, MODULE_LABELS, MODULE_COLORS } from '../contexts/ModuleContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { UpdateBanner } from './UpdateBanner';
import { startCardGlow } from '../lib/cardGlow';
import { startMagnetic } from '../lib/magnetic';

// Use relative URL for API calls to avoid CORS issues in production
const API = '';

import { 
  changePassword, changeEmail, uploadProfilePhoto, deleteProfilePhoto, 
  getAllUsers, toggleUserStatus, generateRecoveryCode,
  getMasterDashboard, getAdminNotes, updateAdminNotes, getAppStatus, 
  updateAppStatus, getActivityLog, broadcastMessage, getMySubscription,
  getUnreadNotifications, markNotificationRead, markAllNotificationsRead,
  createUserRequest, getNotifications
} from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { 
  LayoutDashboard, 
  RefreshCcw, 
  TrendingUp, 
  Receipt, 
  Wallet, 
  CreditCard, 
  Percent,
  Users,
  ShieldCheck,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  BarChart3,
  DollarSign,
  AlertCircle,
  Camera,
  Key,
  Mail,
  Shield,
  Copy,
  Trash2,
  Upload,
  Lock,
  Unlock,
  FileText,
  Activity,
  Send,
  MessageSquare,
  Wrench,
  Eye,
  EyeOff,
  Clock,
  ExternalLink,
  Bell,
  Gift,
  Calendar,
  History,
  RefreshCw,
  Pause,
  Timer,
  Home,
  ArrowLeft
} from 'lucide-react';

// Navigation items for Arbitex modules (Futures and CFD)
const getArbicsxNavItems = (isAdmin, isAdminPrimary, isAdminSecondary, viewOnly = false, isManaged = false, familyView = false) => {
  // family_view acceso (non admin): solo Riepilogo famiglia (+ Impostazioni se non managed). Nessuna pagina operativa.
  if (familyView && !isAdmin) {
    const familyItems = [{ path: '/arbicsx/riepilogo-famiglia', label: 'Riepilogo famiglia', icon: Home }];
    if (!isManaged) {
      familyItems.push({ path: '/arbicsx/impostazioni', label: 'Impostazioni', icon: Settings });
    }
    return familyItems;
  }

  // Se l'utente è in modalità view_only, mostra solo le sezioni consentite
  if (viewOnly) {
    const viewOnlyItems = [
      { path: '/arbicsx/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/arbicsx/profitti', label: 'Storico Profitti', icon: TrendingUp },
      { path: '/arbicsx/pagamenti-per-noi', label: 'I miei profitti', icon: CreditCard },
      { path: '/arbicsx/percentuali-mensili', label: 'Performance Mensile', icon: Percent },
    ];
    // Per view_only: mostra Impostazioni SOLO se NON è "managed"
    if (familyView) {
      viewOnlyItems.push({ path: '/arbicsx/riepilogo-famiglia', label: 'Riepilogo famiglia', icon: Home });
    }
    if (!isManaged) {
      viewOnlyItems.push({ path: '/arbicsx/impostazioni', label: 'Impostazioni', icon: Settings });
    }
    return viewOnlyItems;
  }
  
  const items = [
    { path: '/arbicsx/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/arbicsx/cicli', label: 'I Miei Conti', icon: RefreshCcw },
    { path: '/arbicsx/wallet', label: 'Il Mio Wallet', icon: Wallet },
    { path: '/arbicsx/profitti', label: 'Storico Profitti', icon: TrendingUp },
    { path: '/arbicsx/costi-conti-prop', label: 'Costi da Recuperare', icon: Receipt },
    { path: '/arbicsx/incassi-prop', label: 'Rimborsi Ricevuti', icon: DollarSign },
    { path: '/arbicsx/pagamenti-per-noi', label: 'I miei profitti', icon: CreditCard },
    { path: '/arbicsx/percentuali-mensili', label: 'Performance Mensile', icon: Percent },
  ];
  
  // Admin (primary or secondary) sees Users section
  if (familyView && !isAdmin) {
    items.push({ path: '/arbicsx/riepilogo-famiglia', label: 'Riepilogo famiglia', icon: Home });
  }

  if (isAdminPrimary || isAdminSecondary) {
    items.push({ path: '/arbicsx/utenti', label: 'Utenti', icon: ShieldCheck, adminSection: true });
  }
  
  // Mostra Impostazioni SOLO se NON è "managed"
  if (!isManaged) {
    items.push({ path: '/arbicsx/impostazioni', label: 'Impostazioni', icon: Settings });
  }
  
  return items;
};

// Module logo URLs (PNG)
const MODULE_LOGOS = {
  [MODULES.FUTURES]: 'https://customer-assets.emergentagent.com/job_arbitex-pro/artifacts/yb784z6t_Arbitex%20Futures.png',
  [MODULES.CFD]: 'https://customer-assets.emergentagent.com/job_arbitex-pro/artifacts/v5xq5kqt_Arbitex%20CFD.png',
  [MODULES.ARBICSX_CFD]: 'https://customer-assets.emergentagent.com/job_arbitex-pro/artifacts/qmgw8cfu_Arbicsx%20CFD.png'
};

// Calibrated logo dimensions for equal visual impact
const MODULE_LOGO_SIZES = {
  [MODULES.FUTURES]: { height: '44px', maxWidth: '200px' },      // AntiFutures - larger to match visual weight
  [MODULES.CFD]: { height: '36px', maxWidth: '180px' },          // Arbitex CFD - reference size
  [MODULES.ARBICSX_CFD]: { height: '46px', maxWidth: '200px' }   // Arbicsx - increased to match visual weight
};

const ModuleSection = ({ module, currentModule, onSelectModule, isOpen, onToggle, children }) => {
  const isActive = currentModule === module;
  const logoUrl = MODULE_LOGOS[module];
  const logoSize = MODULE_LOGO_SIZES[module];
  
  // PNG logos - apply mix-blend-mode: screen for Futures to remove black background
  const logoStyle = {
    height: logoSize.height,
    maxWidth: logoSize.maxWidth,
    objectFit: 'contain',
    ...(module === MODULES.FUTURES && { mixBlendMode: 'screen' })
  };
  
  // Get module-specific colors
  const colors = MODULE_COLORS[module];
  
  return (
    <div className="mb-1">
      <button
        onClick={() => {
          onToggle();
          if (!isActive) {
            onSelectModule(module);
          }
        }}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm font-semibold transition-colors hover:bg-gray-800/50"
        style={isActive ? {
          backgroundColor: 'transparent',
          border: '1px solid #282c34'
        } : {}}
        data-testid={`module-${module}-toggle`}
      >
        <div className="flex items-center justify-center flex-1 px-2">
          <img 
            src={logoUrl} 
            alt={MODULE_LABELS[module]}
            style={logoStyle}
            className="flex-shrink-0"
          />
        </div>
        {isOpen ? <ChevronDown size={16} className="text-disabled flex-shrink-0" /> : <ChevronRight size={16} className="text-disabled flex-shrink-0" />}
      </button>
      
      {isOpen && (
        <div className="mt-1 ml-2 pl-2 border-l border-gray-800/50">
          {children}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, isAdmin, refreshUser } = useAuth();
  const { 
    activeModule, 
    setActiveModule, 
    canAccessArbicsx,
    isAdminPrimary,
    isAdminSecondary,
    impersonatedUser,
    stopImpersonation,
    isImpersonating,
    moduleColors
  } = useModule();
  const navigate = useNavigate();
  const location = useLocation();
  const photoInputRef = useRef(null);
  const navRef = useRef(null);
  const [navIndicator, setNavIndicator] = useState({ top: 0, height: 0, visible: false });
  
  // Profile panel state
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [profileTab, setProfileTab] = useState('info'); // 'info', 'password', 'email', 'admin'
  
  // Password change state
  const [passwordData, setPasswordData] = useState({ old: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Email change state
  const [emailData, setEmailData] = useState({ new: '', password: '' });
  const [changingEmail, setChangingEmail] = useState(false);
  
  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localPhoto, setLocalPhoto] = useState(null);
  
  // Admin state
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  
  // Secret Admin Panel state
  const [showSecretPanel, setShowSecretPanel] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [secretPanelPassword, setSecretPanelPassword] = useState('');
  const [secretPanelTab, setSecretPanelTab] = useState('dashboard');
  const [masterStats, setMasterStats] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [appLocked, setAppLocked] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  // User subscription state (for non-admin profile)
  const [userSubscription, setUserSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [userNotifications, setUserNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [requestingAction, setRequestingAction] = useState(false);
  
  // Track which module sections are expanded
  const [expandedModules, setExpandedModules] = useState({
    [MODULES.ARBICSX_CFD]: true
  });

  // Update expanded state when module changes
  useEffect(() => {
    setExpandedModules(prev => ({
      ...prev,
      [activeModule]: true
    }));
  }, [activeModule]);

  // Load subscription for non-admin users in Arbicsx CFD (for sidebar badge)
  useEffect(() => {
    if (!isAdmin && activeModule === MODULES.ARBICSX_CFD) {
      loadUserSubscription();
    }
  }, [isAdmin, activeModule]);

  const handleLogout = () => {
    logout();
    navigate('/');
    if (onClose) onClose();
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  // Profile panel handlers
  const handleOpenProfile = () => {
    setShowProfilePanel(true);
    setProfileTab('info');
    setLocalPhoto(null);
    setPasswordData({ old: '', new: '', confirm: '' });
    setEmailData({ new: '', password: '' });
    setRecoveryCode(null);
    // Load admin users if admin and in Arbicsx module
    if (isAdmin && activeModule === MODULES.ARBICSX_CFD) {
      loadAdminUsers();
    }
    // Load subscription and notifications for non-admin users in Arbicsx CFD
    if (!isAdmin && activeModule === MODULES.ARBICSX_CFD) {
      loadUserSubscription();
      loadUserNotifications();
    }
  };

  const loadAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await getAllUsers();
      setAdminUsers(res.data.filter(u => u.email !== 'eliabonacci92@gmail.com'));
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load subscription info for non-admin users
  const loadUserSubscription = async () => {
    if (isAdmin) return;
    setLoadingSubscription(true);
    try {
      const res = await getMySubscription();
      setUserSubscription(res.data);
    } catch (error) {
      // Silently fail
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Get subscription badge data for sidebar
  const getSubscriptionBadge = () => {
    if (!userSubscription || isAdmin || activeModule !== MODULES.ARBICSX_CFD) return null;
    if (userSubscription.is_free || userSubscription.is_blocked) return null;
    
    const daysLeft = userSubscription.days_left;
    if (daysLeft === undefined || daysLeft === null) return null;
    
    let color, bgColor, text;
    if (daysLeft < 0) {
      color = 'text-red-400';
      bgColor = 'bg-red-500/20 border-red-500/30';
      text = 'Scaduto';
    } else if (daysLeft <= 3) {
      color = 'text-orange-400';
      bgColor = 'bg-orange-500/20 border-orange-500/30';
      text = `${daysLeft}g`;
    } else if (daysLeft <= 7) {
      color = 'text-yellow-400';
      bgColor = 'bg-yellow-500/20 border-yellow-500/30';
      text = `${daysLeft}g`;
    } else {
      color = 'text-usdt';
      bgColor = 'bg-usdt/20 border-usdt/30';
      text = `${daysLeft}g`;
    }
    
    return { color, bgColor, text };
  };

  // Open profile panel directly to subscription tab
  const handleOpenSubscriptionTab = (e) => {
    e.stopPropagation();
    setShowProfilePanel(true);
    setProfileTab('subscription');
    loadUserSubscription();
    loadUserNotifications();
  };

  // Load notification history
  const loadUserNotifications = async () => {
    if (isAdmin) return;
    setLoadingNotifications(true);
    try {
      const res = await getNotifications();
      setUserNotifications(res.data || []);
    } catch (error) {
      // Silently fail
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Handle user request (renewal/suspension)
  const handleUserRequest = async (type) => {
    setRequestingAction(true);
    try {
      await createUserRequest(type);
      toast.success(type === 'renewal' 
        ? 'Richiesta di rinnovo inviata!' 
        : 'Richiesta di sospensione inviata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setRequestingAction(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.old || !passwordData.new || !passwordData.confirm) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('Le password non coincidono');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('La nuova password deve avere almeno 6 caratteri');
      return;
    }
    setChangingPassword(true);
    try {
      await changePassword({ old_password: passwordData.old, new_password: passwordData.new });
      toast.success('Password aggiornata!');
      setPasswordData({ old: '', new: '', confirm: '' });
      setProfileTab('info');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!emailData.new || !emailData.password) {
      toast.error('Compila tutti i campi');
      return;
    }
    setChangingEmail(true);
    try {
      await changeEmail({ new_email: emailData.new, password: emailData.password });
      toast.success('Email aggiornata! Effettua nuovamente il login.');
      setEmailData({ new: '', password: '' });
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio email');
    } finally {
      setChangingEmail(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const res = await uploadProfilePhoto(file);
      setLocalPhoto(res.data.profile_photo);
      if (refreshUser) await refreshUser();
      toast.success('Foto caricata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await deleteProfilePhoto();
      setLocalPhoto(null);
      if (refreshUser) await refreshUser();
      toast.success('Foto rimossa');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleToggleUser = async (userId, currentStatus) => {
    try {
      await toggleUserStatus(userId, !currentStatus);
      setAdminUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_approved: !currentStatus } : u
      ));
      toast.success(`Utente ${!currentStatus ? 'attivato' : 'disattivato'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleGenerateRecoveryCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await generateRecoveryCode();
      setRecoveryCode(res.data.recovery_code);
      toast.success('Codice generato! Salvalo in un posto sicuro.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti!');
  };

  // Secret Admin Panel handlers
  const handleLogoClick = () => {
    if (!isAdmin) return;
    setShowPasswordPrompt(true);
    setSecretPanelPassword('');
  };

  const handleSecretPanelAuth = async () => {
    try {
      // Verify password via the existing verifyPassword function or direct check
      const { verifyPassword } = await import('../contexts/AuthContext').then(m => ({ verifyPassword: m.default }));
      // We'll use the login endpoint to verify - use relative URL to avoid CORS
      const response = await fetch(`/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, password: secretPanelPassword })
      });
      
      if (response.ok) {
        setShowPasswordPrompt(false);
        setShowSecretPanel(true);
        setSecretPanelTab('dashboard');
        loadSecretPanelData();
      } else {
        toast.error('Password non valida');
      }
    } catch (error) {
      toast.error('Errore di autenticazione');
    }
  };

  const loadSecretPanelData = async () => {
    try {
      // Load master dashboard
      const dashRes = await getMasterDashboard();
      setMasterStats(dashRes.data);
      
      // Load admin notes
      const notesRes = await getAdminNotes();
      setAdminNotes(notesRes.data.content || '');
      
      // Load app status
      const statusRes = await getAppStatus();
      setAppLocked(statusRes.data.app_locked);
      setMaintenanceMode(statusRes.data.maintenance_mode);
      
      // Load all users for broadcast target
      const usersRes = await getAllUsers();
      setAdminUsers(usersRes.data.filter(u => u.email !== 'eliabonacci92@gmail.com'));
    } catch (error) {
      console.error('Error loading secret panel data:', error);
    }
  };

  const loadActivityLog = async () => {
    setLoadingLog(true);
    try {
      const res = await getActivityLog(50);
      setActivityLog(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento log');
    } finally {
      setLoadingLog(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateAdminNotes(adminNotes);
      toast.success('Note salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleToggleAppLock = async () => {
    try {
      const newValue = !appLocked;
      await updateAppStatus({ app_locked: newValue });
      setAppLocked(newValue);
      toast.success(newValue ? 'App bloccata' : 'App sbloccata');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      const newValue = !maintenanceMode;
      await updateAppStatus({ maintenance_mode: newValue });
      setMaintenanceMode(newValue);
      toast.success(newValue ? 'Manutenzione attivata' : 'Manutenzione disattivata');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastMsg.trim()) {
      toast.error('Inserisci un messaggio');
      return;
    }
    setSendingBroadcast(true);
    try {
      await broadcastMessage(broadcastMsg, broadcastTarget);
      toast.success('Messaggio inviato!');
      setBroadcastMsg('');
      setBroadcastTarget('all');
    } catch (error) {
      toast.error('Errore nell\'invio');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const handleModuleSelect = (module) => {
    // Stop impersonation when switching modules
    if (isImpersonating && module !== MODULES.ARBICSX_CFD) {
      stopImpersonation();
    }
    
    setActiveModule(module);
    navigate('/arbicsx/dashboard');
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (onClose) onClose();
  }, [location.pathname]);

  // Indicatore della voce attiva che scivola (misurato sul link con aria-current)
  useEffect(() => {
    const nav = navRef.current;
    const panel = nav && nav.closest('aside');
    let raf1 = 0, raf2 = 0;

    const measure = () => {
      const active = nav && nav.querySelector('[aria-current="page"]');
      if (!nav || !active) {
        setNavIndicator((prev) => ({ ...prev, visible: false }));
        return;
      }
      const navBox = nav.getBoundingClientRect();
      const box = active.getBoundingClientRect();
      setNavIndicator({
        top: box.top - navBox.top + nav.scrollTop + 4,
        height: Math.max(12, box.height - 8),
        visible: true,
      });
    };

    const onPanelEnd = (e) => {
      if (e.target === panel) measure();
    };
    // le voci entrano a cascata traslando in verticale: rimisuro a cascata finita
    const onItemAnimEnd = (e) => {
      if (e.animationName === 'ht-fade-up') measure();
    };

    measure();
    // il pannello del telefono scivola: rimisuro a layout fermo
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    if (panel) panel.addEventListener('transitionend', onPanelEnd);
    if (nav) nav.addEventListener('animationend', onItemAnimEnd);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (panel) panel.removeEventListener('transitionend', onPanelEnd);
      if (nav) nav.removeEventListener('animationend', onItemAnimEnd);
    };
  }, [location.pathname, isOpen, activeModule]);

  const renderNavItems = (items, module) => {
    return (
      <ul className={`space-y-0.5 ${isOpen ? 'sidebar-cascade' : ''}`}>
        {items.map((item) => (
          <li key={item.path}>
            {item.adminSection && (
              <div className="pt-3 pb-1.5 mt-2 border-t border-gray-800" data-testid="sidebar-admin-section-label">
                <span className="sidebar-admin-label px-3">Amministrazione</span>
              </div>
            )}
            <NavLink
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `sidebar-nav-item flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  isActive && activeModule === module
                    ? 'text-white sidebar-item-active'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                } ${item.adminSection ? 'sidebar-admin-item' : ''}`
              }
              data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
            >
              <item.icon size={item.adminSection ? 18 : 15} className="flex-shrink-0" style={item.adminSection ? { color: '#26A17B' } : undefined} />
              <span className="truncate flex-1">{item.label}</span>
              {item.adminSection && <ChevronRight size={16} style={{ color: '#26A17B' }} className="flex-shrink-0" />}
            </NavLink>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`
        fixed left-0 top-0 h-screen w-[75vw] max-w-[280px] bg-[#0d1117] border-r border-[#282c34] flex flex-col z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:w-[240px]
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo - Clickable for admin secret panel (ONLY when not impersonating) */}
        <div className="px-3 py-2 border-b border-gray-800/50 flex items-center justify-center relative">
          <button 
            onClick={isAdmin && !isImpersonating ? handleLogoClick : undefined}
            className={`flex items-center gap-2 ${isAdmin && !isImpersonating ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'}`}
            title={isAdmin && !isImpersonating ? 'Pannello Admin Segreto' : ''}
          >
            <img 
              src="/icons/ht-logo-v3.png" 
              alt="HT" 
              className="w-9 h-9 rounded-[9px] flex-shrink-0 object-contain ht-logo ht-logo-sm"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 rounded-lg items-center justify-center flex-shrink-0 hidden bg-[#0c1a15] border border-usdt/40">
              <span className="text-usdt font-bold text-xs font-heading">HT</span>
            </div>
            <div className="text-left">
              <h1 className="text-sm font-bold text-white font-heading leading-tight">Hedging Tracker</h1>
              <p className="text-[10px] text-disabled leading-tight">Gestione Profitti</p>
            </div>
          </button>
          <button 
            onClick={onClose}
            className="lg:hidden p-1 text-gray-400 hover:text-white absolute right-2 top-2"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation with Module Sections */}
        <nav ref={navRef} className="flex-1 py-4 overflow-y-auto relative">
          {navIndicator.visible && (
            <span
              className="sidebar-indicator"
              style={{ top: navIndicator.top, height: navIndicator.height }}
              data-testid="sidebar-active-indicator"
            />
          )}
          <div className="px-2">
            {/* Arbicsx CFD Section - All approved users + admin */}
            {canAccessArbicsx && (
              <ModuleSection
                module={MODULES.ARBICSX_CFD}
                currentModule={activeModule}
                onSelectModule={handleModuleSelect}
                isOpen={expandedModules[MODULES.ARBICSX_CFD]}
                onToggle={() => toggleModule(MODULES.ARBICSX_CFD)}
              >
                {renderNavItems(getArbicsxNavItems(
                  isAdmin && !isImpersonating,
                  isAdminPrimary && !isImpersonating,
                  isAdminSecondary && !isImpersonating,
                  // Se admin sta impersonando, ignora view_only e mostra tutto
                  isImpersonating ? false : user?.view_only,
                  // Se admin sta impersonando, ignora management_type. Altrimenti, mostra restrizioni per "managed"
                  isImpersonating ? false : user?.management_type === 'managed',
                  // Riepilogo famiglia: solo per l'utente loggato (mai in impersonazione)
                  isImpersonating ? false : !!user?.family_view
                ), MODULES.ARBICSX_CFD)}
              </ModuleSection>
            )}
          </div>
        </nav>

        {/* User - Clickable to open profile panel */}
        <div className="p-3 border-t border-gray-800/50">
          <div className="flex items-center gap-2 mb-2 w-full">
            {/* Per utenti "managed" (non impersonati), disabilita il click sul profilo */}
            {(!isImpersonating && user?.management_type === 'managed') ? (
              // Versione non cliccabile per utenti "managed"
              <div
                className="flex items-center gap-2 flex-1 rounded-lg p-1 -m-1 cursor-default opacity-80"
                data-testid="profile-panel-disabled"
              >
                <Avatar className="h-8 w-8 border border-gray-700">
                  {user?.profile_photo ? (
                    <AvatarImage 
                      src={user?.profile_photo} 
                      alt={user?.name} 
                    />
                  ) : null}
                  <AvatarFallback style={{ backgroundColor: moduleColors.primary }} className="text-white text-xs font-medium">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-white truncate">
                    {user?.name || 'Utente'}
                  </p>
                  <p className="text-[10px] text-disabled truncate">
                    {user?.email || ''}
                  </p>
                </div>
              </div>
            ) : (
              // Versione cliccabile normale
              <button
                onClick={handleOpenProfile}
                className="flex items-center gap-2 flex-1 hover:bg-gray-800/50 rounded-lg p-1 -m-1 transition-colors cursor-pointer"
                data-testid="profile-panel-btn"
              >
                <Avatar className="h-8 w-8 border border-gray-700">
                  {/* When impersonating, ALWAYS show impersonated user's photo */}
                  {(localPhoto || (isImpersonating ? impersonatedUser?.profile_photo : user?.profile_photo)) ? (
                    <AvatarImage 
                      src={localPhoto || (isImpersonating ? impersonatedUser?.profile_photo : user?.profile_photo)} 
                      alt={isImpersonating ? impersonatedUser?.name : user?.name} 
                    />
                  ) : null}
                  <AvatarFallback style={{ backgroundColor: moduleColors.primary }} className="text-white text-xs font-medium">
                    {(isImpersonating ? impersonatedUser?.name?.[0] : user?.name?.[0])?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-white truncate">
                    {isImpersonating ? impersonatedUser?.name : user?.name || 'Utente'}
                  </p>
                  <p className="text-[10px] text-disabled truncate">
                    {isImpersonating ? impersonatedUser?.email : user?.email || ''}
                  </p>
                  {/* Only show Admin badge when NOT impersonating */}
                  {isAdmin && !isImpersonating && <p className="text-[9px]" style={{ color: moduleColors.text }}>Admin</p>}
                </div>
                <Settings size={14} className="text-disabled" />
              </button>
            )}
            {/* Subscription days badge - non-admin users in Arbicsx CFD only */}
            {!isAdmin && !isImpersonating && activeModule === MODULES.ARBICSX_CFD && getSubscriptionBadge() && (
              <button
                onClick={handleOpenSubscriptionTab}
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${getSubscriptionBadge().bgColor} ${getSubscriptionBadge().color} hover:opacity-80 transition-opacity flex-shrink-0`}
                title="Dettagli abbonamento"
                data-testid="subscription-badge"
              >
                {getSubscriptionBadge().text}
              </button>
            )}
          </div>
          <LinkedProfilesSwitcher hidden={isImpersonating} accentColor={moduleColors.text} />
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-400 hover:text-white hover:bg-[#11161d]/50 rounded transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            <span>Esci</span>
          </button>
        </div>

        {/* Profile Panel Dialog */}
        <Dialog open={showProfilePanel} onOpenChange={setShowProfilePanel}>
          <DialogContent className="ht-panel text-white max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <User size={20} style={{ color: moduleColors.text }} />
                {isImpersonating ? `Profilo di ${impersonatedUser?.name}` : 'Profilo'}
              </DialogTitle>
            </DialogHeader>
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={photoInputRef}
              onChange={handlePhotoSelect}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />

            {/* Tab Navigation - Limited when impersonating */}
            <div className="flex gap-1 border-b border-[#282c34] mb-4">
              <button
                onClick={() => setProfileTab('info')}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  profileTab === 'info' ? 'border-current text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
                style={profileTab === 'info' ? { borderColor: moduleColors.primary } : {}}
              >
                Info
              </button>
              {/* Password/Email/Admin tabs only when NOT impersonating */}
              {!isImpersonating && (
                <>
                  <button
                    onClick={() => setProfileTab('password')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      profileTab === 'password' ? 'border-current text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                    style={profileTab === 'password' ? { borderColor: moduleColors.primary } : {}}
                  >
                    Password
                  </button>
                  <button
                    onClick={() => setProfileTab('email')}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                      profileTab === 'email' ? 'border-current text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
                    }`}
                    style={profileTab === 'email' ? { borderColor: moduleColors.primary } : {}}
                  >
                    Email
                  </button>
                  {/* Subscription tab for non-admin users in Arbicsx CFD */}
                  {!isAdmin && activeModule === MODULES.ARBICSX_CFD && (
                    <button
                      onClick={() => { setProfileTab('subscription'); loadUserSubscription(); loadUserNotifications(); }}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        profileTab === 'subscription' ? 'border-current text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
                      }`}
                      style={profileTab === 'subscription' ? { borderColor: moduleColors.primary } : {}}
                    >
                      Abbonamento
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => { setProfileTab('admin'); if (activeModule === MODULES.ARBICSX_CFD) loadAdminUsers(); }}
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        profileTab === 'admin' ? 'border-current text-white' : 'border-transparent text-gray-400 hover:text-gray-300'
                      }`}
                      style={profileTab === 'admin' ? { borderColor: moduleColors.primary } : {}}
                    >
                      Admin
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Info Tab */}
            {profileTab === 'info' && (
              <div className="space-y-4">
                {/* Profile Photo - Show impersonated user's data when impersonating */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar className="h-20 w-20 border-2 border-[#282c34]">
                      {(localPhoto || (isImpersonating ? impersonatedUser?.profile_photo : user?.profile_photo)) ? (
                        <AvatarImage 
                          src={localPhoto || (isImpersonating ? impersonatedUser?.profile_photo : user?.profile_photo)} 
                          alt={isImpersonating ? impersonatedUser?.name : user?.name} 
                        />
                      ) : null}
                      <AvatarFallback className="text-2xl bg-[#11161d] ht-pos">
                        {(isImpersonating ? impersonatedUser?.name?.[0] : user?.name?.[0])?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    {/* Only show camera button if NOT impersonating */}
                    {!isImpersonating && (
                      <button
                        onClick={() => photoInputRef.current?.click()}
                        className="absolute bottom-0 right-0 p-1.5 rounded-full border border-gray-600 hover:bg-gray-700 transition-colors"
                        style={{ backgroundColor: moduleColors.primaryLight }}
                        disabled={uploadingPhoto}
                      >
                        <Camera size={12} style={{ color: moduleColors.text }} />
                      </button>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{isImpersonating ? impersonatedUser?.name : user?.name}</p>
                    <p className="text-sm text-gray-400">{isImpersonating ? impersonatedUser?.email : user?.email}</p>
                    {!isImpersonating && isAdmin && <p className="text-xs mt-1" style={{ color: moduleColors.text }}>Amministratore</p>}
                    {isImpersonating && (
                      <p className="text-xs mt-1 ht-warn">Visualizzazione come utente</p>
                    )}
                    {/* Photo buttons only when NOT impersonating */}
                    {!isImpersonating && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="text-xs gap-1 border-[#282c34]"
                        >
                          <Upload size={12} />
                          {uploadingPhoto ? 'Carico...' : 'Carica'}
                        </Button>
                        {(localPhoto || user?.profile_photo) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDeletePhoto}
                            className="text-xs gap-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 size={12} />
                            Rimuovi
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions section */}
                <div className="pt-4 border-t border-[#282c34] space-y-2">
                  {isImpersonating ? (
                    <Button
                      onClick={() => { stopImpersonation(); setShowProfilePanel(false); }}
                      variant="outline"
                      className="w-full ht-btn ht-btn-warn"
                    >
                      <User size={16} />
                      Torna al tuo profilo
                    </Button>
                  ) : (
                    <Button
                      onClick={() => { handleLogout(); setShowProfilePanel(false); }}
                      variant="outline"
                      className="ht-btn w-full"
                    >
                      <LogOut size={16} />
                      Logout
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Password Tab - Only when NOT impersonating */}
            {profileTab === 'password' && !isImpersonating && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-400">Password attuale</Label>
                  <Input
                    type="password"
                    value={passwordData.old}
                    onChange={(e) => setPasswordData({ ...passwordData, old: e.target.value })}
                    placeholder="••••••••"
                    className="bg-[#11161d] border-[#282c34]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-400">Nuova password</Label>
                  <Input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    placeholder="Min. 6 caratteri"
                    className="bg-[#11161d] border-[#282c34]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-400">Conferma password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    placeholder="Ripeti la nuova password"
                    className="bg-[#11161d] border-[#282c34]"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="w-full gap-2"
                  style={{ backgroundColor: moduleColors.primary }}
                >
                  <Key size={16} />
                  {changingPassword ? 'Aggiornamento...' : 'Cambia Password'}
                </Button>
              </div>
            )}

            {/* Email Tab - Only when NOT impersonating */}
            {profileTab === 'email' && !isImpersonating && (
              <div className="space-y-4">
                <div className="bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)] rounded-lg p-3 text-sm ht-warn">
                  Dopo il cambio email dovrai effettuare nuovamente il login.
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-400">Nuova email</Label>
                  <Input
                    type="email"
                    value={emailData.new}
                    onChange={(e) => setEmailData({ ...emailData, new: e.target.value })}
                    placeholder="nuova@email.com"
                    className="bg-[#11161d] border-[#282c34]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-400">Password (conferma)</Label>
                  <Input
                    type="password"
                    value={emailData.password}
                    onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                    placeholder="Inserisci la tua password"
                    className="bg-[#11161d] border-[#282c34]"
                  />
                </div>
                <Button
                  onClick={handleChangeEmail}
                  disabled={changingEmail}
                  className="w-full gap-2"
                  style={{ backgroundColor: moduleColors.primary }}
                >
                  <Mail size={16} />
                  {changingEmail ? 'Aggiornamento...' : 'Cambia Email'}
                </Button>
              </div>
            )}

            {/* Admin Tab - Only when NOT impersonating */}
            {profileTab === 'admin' && isAdmin && !isImpersonating && (
              <div className="space-y-4">
                {/* Recovery Code - Always visible for admin */}
                <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield size={18} className="ht-warn" />
                    <h3 className="font-medium">Codice di Recupero</h3>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    Genera un codice per resettare la password admin dalla pagina di login. Salvalo in un posto sicuro — sarà visibile solo una volta.
                  </p>
                  
                  {recoveryCode ? (
                    <div className="space-y-2">
                      <div className="bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)] rounded p-3">
                        <p className="text-xs ht-pos mb-2">Codice generato! Copialo e salvalo.</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-black/50 px-2 py-1 rounded text-xs font-mono ht-pos break-all">
                            {recoveryCode}
                          </code>
                          <button
                            onClick={() => copyToClipboard(recoveryCode)}
                            className="p-2 hover:bg-gray-700 rounded transition-colors"
                          >
                            <Copy size={14} className="text-gray-400" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-red-400">⚠️ Non sarà più visibile dopo aver chiuso questo pannello</p>
                    </div>
                  ) : (
                    <Button
                      onClick={handleGenerateRecoveryCode}
                      disabled={generatingCode}
                      variant="outline"
                      className="w-full ht-btn ht-btn-warn"
                    >
                      <Key size={14} />
                      {generatingCode ? 'Generazione...' : 'Genera Codice di Recupero'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Subscription Tab - For non-admin users in Arbicsx CFD */}
            {profileTab === 'subscription' && !isAdmin && !isImpersonating && activeModule === MODULES.ARBICSX_CFD && (
              <div className="space-y-4">
                {loadingSubscription ? (
                  <p className="text-sm text-gray-400 text-center py-4">Caricamento...</p>
                ) : userSubscription ? (
                  <>
                    {/* Subscription Status */}
                    <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CreditCard size={18} style={{ color: moduleColors.text }} />
                        <h3 className="font-medium">Il mio abbonamento</h3>
                      </div>
                      
                      <div className="space-y-3">
                        {/* Status badge */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Stato</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            userSubscription.is_blocked ? 'bg-red-500/20 text-red-400' :
                            userSubscription.is_suspended ? 'bg-orange-500/20 text-orange-400' :
                            userSubscription.is_free ? 'bg-gray-500/20 text-gray-400' :
                            userSubscription.status === 'attivo' ? 'bg-[rgba(61,219,169,.14)] ht-pos' :
                            userSubscription.status === 'in_scadenza' ? 'bg-[rgba(240,180,90,.14)] ht-warn' :
                            userSubscription.status === 'scaduto' ? 'bg-red-500/20 text-red-400' :
                            userSubscription.status === 'prova' ? 'bg-[rgba(40,44,52,.6)] text-[#8b919c]' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {userSubscription.is_blocked ? 'Bloccato' :
                             userSubscription.is_suspended ? 'Sospeso' :
                             userSubscription.is_free ? 'Gratuito' :
                             userSubscription.status === 'attivo' ? 'Attivo' :
                             userSubscription.status === 'in_scadenza' ? 'In scadenza' :
                             userSubscription.status === 'scaduto' ? 'Scaduto' :
                             userSubscription.status === 'prova' ? 'In prova' :
                             'Gratuito'}
                          </span>
                        </div>
                        
                        {/* Start date */}
                        {userSubscription.subscription_start_date && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Data inizio</span>
                            <span className="text-sm text-white">
                              {formatDate(userSubscription.subscription_start_date)}
                            </span>
                          </div>
                        )}
                        
                        {/* Expiry date */}
                        {userSubscription.subscription_expires && !userSubscription.is_free && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Data scadenza</span>
                            <span className="text-sm text-white">
                              {formatDate(userSubscription.subscription_expires)}
                            </span>
                          </div>
                        )}
                        
                        {/* Days countdown */}
                        {userSubscription.days_left !== undefined && !userSubscription.is_free && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Giorni rimanenti</span>
                            <span className={`text-sm font-medium ${
                              userSubscription.days_left < 0 ? 'text-red-400' :
                              userSubscription.days_left <= 3 ? 'text-orange-400' :
                              userSubscription.days_left <= 7 ? 'ht-warn' :
                              'ht-pos'
                            }`}>
                              {userSubscription.days_left < 0 
                                ? `Scaduto da ${Math.abs(userSubscription.days_left)} giorni`
                                : userSubscription.days_left === 0 
                                  ? 'Scade oggi'
                                  : `${userSubscription.days_left} giorni`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUserRequest('renewal')}
                        disabled={requestingAction}
                        className="ht-btn ht-btn-primary flex-1"
                      >
                        <RefreshCw size={14} />
                        Richiedi Rinnovo
                      </Button>
                      <Button
                        onClick={() => handleUserRequest('suspension')}
                        disabled={requestingAction}
                        variant="outline"
                        className="flex-1 gap-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      >
                        <Pause size={14} />
                        Richiedi Sospensione
                      </Button>
                    </div>

                    {/* Telegram Connection */}
                    <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Send size={18} className="text-[#8b919c]" />
                        <h3 className="font-medium">Notifiche Telegram</h3>
                      </div>
                      {userSubscription.telegram_chat_id ? (
                        <div className="flex items-center gap-2 ht-pos">
                          <div className="w-2 h-2 bg-[#3ddba9] rounded-full"></div>
                          <span className="text-sm">Telegram collegato</span>
                        </div>
                      ) : !userSubscription.user_id ? (
                        <p className="text-sm text-gray-400">Caricamento...</p>
                      ) : (
                        <Button
                          onClick={() => window.open(`https://t.me/HedgingTrakerBot?start=${userSubscription.user_id}`, '_blank')}
                          variant="outline"
                          className="w-full ht-btn"
                        >
                          <Send size={14} />
                          Collega Telegram
                        </Button>
                      )}
                    </div>

                    {/* Notification History */}
                    <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <History size={18} className="text-[#8b919c]" />
                          <h3 className="font-medium">Storico Notifiche</h3>
                        </div>
                        {userNotifications.length > 0 && (
                          <span className="text-xs text-disabled">{userNotifications.length} notifiche</span>
                        )}
                      </div>
                      
                      {loadingNotifications ? (
                        <p className="text-sm text-gray-400">Caricamento...</p>
                      ) : userNotifications.length === 0 ? (
                        <p className="text-sm text-disabled text-center py-2">Nessuna notifica</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {userNotifications.slice(0, 10).map(notif => (
                            <div key={notif.id} className="p-2 bg-gray-900/50 rounded-lg">
                              <p className="text-sm text-white">{notif.message}</p>
                              <p className="text-xs text-disabled mt-1">
                                {new Date(notif.created_at).toLocaleDateString('it-IT', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-disabled text-center py-4">Errore nel caricamento dell'abbonamento</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Password Prompt Dialog */}
        <Dialog open={showPasswordPrompt} onOpenChange={setShowPasswordPrompt}>
          <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-gray-300">
                <Lock size={18} className="text-amber-500" />
                Accesso Riservato
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-disabled">Inserisci la password admin per accedere.</p>
              <Input
                type="password"
                value={secretPanelPassword}
                onChange={(e) => setSecretPanelPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSecretPanelAuth()}
                placeholder="Password"
                className="bg-[#11161d] border-[#282c34] text-white"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowPasswordPrompt(false)}
                  variant="outline"
                  className="ht-btn flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSecretPanelAuth}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  Accedi
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Secret Admin Panel */}
        <Dialog open={showSecretPanel} onOpenChange={setShowSecretPanel}>
          <DialogContent 
            className="bg-[#1a1a1a] border-gray-600 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="ht-panel-title flex items-center gap-2 text-base">
                <Shield size={20} />
                Pannello Admin Segreto
              </DialogTitle>
            </DialogHeader>
            
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-1 border-b border-gray-600 mb-4 pb-2">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                { id: 'notes', label: 'Note', icon: FileText },
                { id: 'security', label: 'Sicurezza', icon: Shield },
                { id: 'log', label: 'Log', icon: Activity },
                { id: 'messages', label: 'Messaggi', icon: MessageSquare },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSecretPanelTab(tab.id);
                    if (tab.id === 'log') loadActivityLog();
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-colors ${
                    secretPanelTab === tab.id 
                      ? 'bg-[rgba(61,219,169,.08)] text-[#3ddba9] border border-[rgba(61,219,169,.5)]' 
                      : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Dashboard Tab */}
            {secretPanelTab === 'dashboard' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Dashboard Master</h3>
                {masterStats ? (
                  <div className="space-y-4">
                    {/* Profitti separati */}
                    <div className="grid grid-cols-1 gap-3">
                      <div className="bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)] rounded-lg p-4">
                        <p className="text-xs text-[#8b919c] uppercase tracking-wider mb-1">Profitto Personale</p>
                        <p className="text-xl font-mono ht-pos">{formatCurrency(masterStats.admin_profit)}</p>
                        <p className="text-xs font-mono text-[#5f6672] mt-1">
                          ≈ €{((masterStats.admin_profit || 0) * 0.85).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-4">
                        <p className="text-xs text-[#8b919c] uppercase tracking-wider mb-1">Profitto Utenti</p>
                        <p className="text-xl font-mono text-[#e6e9ee]">{formatCurrency(masterStats.users_profit)}</p>
                        <p className="text-xs font-mono text-[#5f6672] mt-1">
                          ≈ €{((masterStats.users_profit || 0) * 0.85).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    
                    {/* Stats grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-3">
                        <p className="text-xs text-gray-400">Cicli Attivi</p>
                        <p className="text-lg font-mono text-white">{masterStats.active_cycles}</p>
                      </div>
                      <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-3">
                        <p className="text-xs text-gray-400">Crediti CFD</p>
                        <p className="text-lg font-mono text-[#e6e9ee]">{formatCurrency(masterStats.cfd_credits)}</p>
                      </div>
                      <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-3">
                        <p className="text-xs text-gray-400">Crediti Arbicsx</p>
                        <p className="text-lg font-mono ht-pos">{formatCurrency(masterStats.arbicsx_credits)}</p>
                      </div>
                      <div className="bg-[#11161d] border border-[#282c34] rounded-lg p-3">
                        <p className="text-xs text-gray-400">Utenti Attivi</p>
                        <p className="text-lg font-mono text-white">{masterStats.active_arbicsx_users}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-disabled">Caricamento...</p>
                )}
              </div>
            )}

            {/* Notes Tab */}
            {secretPanelTab === 'notes' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Note Private</h3>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Scrivi qui le tue note private..."
                  className="bg-[#11161d] border-[#282c34] text-white min-h-[200px] text-sm"
                />
                <Button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  {savingNotes ? 'Salvataggio...' : 'Salva Note'}
                </Button>
              </div>
            )}

            {/* Security Tab */}
            {secretPanelTab === 'security' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Controlli di Sicurezza</h3>
                
                {/* App Lock */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {appLocked ? <Lock size={20} className="text-red-400" /> : <Unlock size={20} className="text-gray-400" />}
                      <div>
                        <p className="font-medium text-white">PIN di Emergenza</p>
                        <p className="text-xs text-gray-400">Blocca l'accesso a tutti gli utenti</p>
                      </div>
                    </div>
                    <Switch
                      checked={appLocked}
                      onCheckedChange={handleToggleAppLock}
                    />
                  </div>
                  {appLocked && (
                    <p className="mt-2 text-xs text-red-400">⚠️ App attualmente bloccata - Solo admin può accedere</p>
                  )}
                </div>

                {/* Maintenance Mode */}
                <div className="bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Wrench size={20} className={maintenanceMode ? 'ht-warn' : 'text-gray-400'} />
                      <div>
                        <p className="font-medium text-white">Modalità Manutenzione</p>
                        <p className="text-xs text-gray-400">Mostra messaggio "App in manutenzione"</p>
                      </div>
                    </div>
                    <Switch
                      checked={maintenanceMode}
                      onCheckedChange={handleToggleMaintenance}
                    />
                  </div>
                  {maintenanceMode && (
                    <p className="mt-2 text-xs ht-warn">🔧 Manutenzione attiva - Gli utenti vedono un avviso</p>
                  )}
                </div>
              </div>
            )}

            {/* Activity Log Tab */}
            {secretPanelTab === 'log' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Log Attività</h3>
                {loadingLog ? (
                  <p className="text-sm text-disabled">Caricamento...</p>
                ) : activityLog.length === 0 ? (
                  <p className="text-sm text-disabled">Nessuna attività registrata</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {activityLog.map((log, i) => (
                      <div key={i} className="bg-[#11161d] border border-[#282c34] rounded p-2 text-xs">
                        <div className="flex justify-between items-start">
                          <span className="ht-warn font-medium">{log.action}</span>
                          <span className="text-disabled">{new Date(log.timestamp).toLocaleString('it-IT')}</span>
                        </div>
                        <p className="text-gray-400 mt-1">{log.user_email}</p>
                        {log.details && <p className="text-disabled mt-0.5">{log.details}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages Tab */}
            {secretPanelTab === 'messages' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Messaggi agli Utenti</h3>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-400">Destinatario</Label>
                    <select
                      value={broadcastTarget}
                      onChange={(e) => setBroadcastTarget(e.target.value)}
                      className="w-full mt-1 bg-[#11161d] border border-[#282c34] rounded-[10px] px-3 py-2 text-sm text-white"
                    >
                      <option value="all">Tutti gli utenti</option>
                      {adminUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-gray-400">Messaggio</Label>
                    <Textarea
                      value={broadcastMsg}
                      onChange={(e) => setBroadcastMsg(e.target.value)}
                      placeholder="Scrivi il messaggio da inviare..."
                      className="mt-1 bg-[#11161d] border-[#282c34] text-white min-h-[100px]"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSendBroadcast}
                    disabled={sendingBroadcast}
                    className="w-full ht-btn ht-btn-primary gap-2"
                  >
                    <Send size={16} />
                    {sendingBroadcast ? 'Invio...' : 'Invia Messaggio'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </aside>
    </>
  );
};

// Impersonation Banner Component
const ImpersonationBanner = () => {
  const { impersonatedUser, stopImpersonation, isImpersonating } = useModule();
  const navigate = useNavigate();
  
  if (!isImpersonating) return null;
  
  const handleBack = () => {
    stopImpersonation();
    navigate('/arbicsx/utenti');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  return (
    <div 
      className="fixed top-[68px] right-3 sm:top-3 z-50 ht-panel bg-[#0d1117]/95 backdrop-blur-sm px-3 py-2 flex items-center gap-2.5 max-w-[calc(100vw-24px)]"
      data-testid="impersonation-badge"
    >
      {/* User Photo or Initials - unico dettaglio giallo */}
      <div className="w-7 h-7 rounded-full overflow-hidden bg-[rgba(240,180,90,.10)] border border-[rgba(240,180,90,.45)] flex items-center justify-center flex-shrink-0">
        {impersonatedUser?.profile_photo ? (
          <img 
            src={impersonatedUser.profile_photo} 
            alt="" 
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-[10px] ht-warn font-medium">
            {getInitials(impersonatedUser?.name)}
          </span>
        )}
      </div>
      <div className="min-w-0 leading-tight">
        <div className="text-[9px] uppercase tracking-[.12em] ht-warn font-medium">
          Stai guardando come
        </div>
        <div className="text-xs text-[#e6e9ee] font-medium max-w-[130px] truncate">
          {impersonatedUser?.name}
        </div>
      </div>
      <button
        onClick={handleBack}
        className="btn-magnetic ht-btn ht-btn-sm ht-btn-primary whitespace-nowrap flex-shrink-0"
        data-magnetic
        data-testid="stop-impersonation-btn"
      >
        <ArrowLeft size={12} />
        Torna
      </button>
    </div>
  );
};

// Admin Message Popup Component
const AdminMessagePopup = () => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  
  // Import API
  const fetchMessages = async () => {
    if (!isAuthenticated || !user) return;
    try {
      const { getUserMessages, dismissMessage } = await import('../lib/api');
      const res = await getUserMessages();
      if (res.data && res.data.length > 0) {
        setMessages(res.data);
        setCurrentMessage(res.data[0]);
      }
    } catch (error) {
      // Silently fail
    }
  };
  
  useEffect(() => {
    fetchMessages();
    // Poll every 30 seconds for new messages
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);
  
  const handleDismiss = async () => {
    if (!currentMessage) return;
    try {
      const { dismissMessage } = await import('../lib/api');
      await dismissMessage(currentMessage.id);
      const remaining = messages.filter(m => m.id !== currentMessage.id);
      setMessages(remaining);
      setCurrentMessage(remaining.length > 0 ? remaining[0] : null);
    } catch (error) {
      // Silently fail
    }
  };
  
  if (!currentMessage) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-[#111827] border border-[rgba(240,180,90,.4)] rounded-xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="ht-warn" />
            <span className="font-semibold text-white">Messaggio dall'Amministratore</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-gray-300 text-sm whitespace-pre-wrap">{currentMessage.message}</p>
        <p className="text-xs text-disabled mt-4">
          {new Date(currentMessage.timestamp).toLocaleString('it-IT')}
        </p>
      </div>
    </div>
  );
};

// Notification Popup Component (Arbicsx CFD only)
const NotificationPopup = () => {
  const { activeModule, isImpersonating } = useModule();
  const { isAdmin, isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  
  // Only show for non-admin users in Arbicsx CFD module
  const shouldCheck = isAuthenticated && !isAdmin && !isImpersonating && activeModule === MODULES.ARBICSX_CFD;
  
  useEffect(() => {
    if (!shouldCheck) {
      setNotifications([]);
      setCurrentNotification(null);
      return;
    }
    
    const fetchNotifications = async () => {
      try {
        const res = await getUnreadNotifications();
        const unread = res.data || [];
        if (unread.length > 0 && !currentNotification) {
          setNotifications(unread);
          setCurrentNotification(unread[0]);
        }
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchNotifications();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [shouldCheck, user?.id]);
  
  const handleClose = async () => {
    if (currentNotification) {
      try {
        await markNotificationRead(currentNotification.id);
      } catch (error) {
        // Silently fail
      }
      
      // Show next notification if any
      const remaining = notifications.filter(n => n.id !== currentNotification.id);
      setNotifications(remaining);
      if (remaining.length > 0) {
        setCurrentNotification(remaining[0]);
      } else {
        setCurrentNotification(null);
      }
    }
  };
  
  if (!currentNotification) return null;
  
  // Get notification icon tone based on type (colore solo sull'icona)
  const getNotificationStyle = (type) => {
    switch (type) {
      case 'renewal':
        return { tone: 'pos', icon: <CreditCard size={22} /> };
      case 'set_free':
        return { tone: 'pos', icon: <Gift size={22} /> };
      case 'suspended':
        return { tone: 'warn', icon: <Pause size={22} /> };
      case 'reactivated':
      case 'unblocked':
        return { tone: 'pos', icon: <RefreshCw size={22} /> };
      case 'blocked':
        return { tone: 'alert', icon: <Lock size={22} /> };
      case 'trial_started':
        return { tone: 'neutral', icon: <Timer size={22} /> };
      default:
        return { tone: 'neutral', icon: <Bell size={22} /> };
    }
  };
  
  const style = getNotificationStyle(currentNotification.notification_type);
  const TONES = {
    pos: { text: 'ht-pos', ring: 'border-[rgba(61,219,169,.45)] bg-[rgba(61,219,169,.07)]', btn: 'ht-btn-primary' },
    warn: { text: 'ht-warn', ring: 'border-[rgba(240,180,90,.45)] bg-[rgba(240,180,90,.07)]', btn: 'ht-btn-warn' },
    alert: { text: 'ht-alert', ring: 'border-[rgba(248,113,113,.45)] bg-[rgba(248,113,113,.07)]', btn: 'ht-btn-danger' },
    neutral: { text: 'text-[#8b919c]', ring: 'border-[#282c34] bg-[#11161d]', btn: '' },
  };
  const tone = TONES[style.tone];
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="notification-popup">
      <div className="ht-panel max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 ${tone.ring} ${tone.text}`}>
                {style.icon}
              </div>
              <div>
                <h3 className="font-semibold text-base text-[#e6e9ee]">Notifica</h3>
                <p className="text-xs text-[#8b919c] ht-num">
                  {new Date(currentNotification.created_at).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="ht-btn ht-btn-icon"
              data-testid="notification-close-btn"
            >
              <X size={16} />
            </button>
          </div>
          <p className="mt-4 text-sm text-[#e6e9ee] leading-relaxed">{currentNotification.message}</p>
          {notifications.length > 1 && (
            <p className="mt-3 text-xs text-[#5f6672]">
              +{notifications.length - 1} altre notifiche
            </p>
          )}
        </div>
        <div className="border-t border-[#282c34] bg-[#11161d] px-5 sm:px-6 py-3 flex justify-end">
          <button
            onClick={handleClose}
            className={`ht-btn ${tone.btn || 'ht-btn-primary'}`}
            data-testid="notification-ack-btn"
          >
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
};

// Subscription Expiry Banner Component (Arbicsx CFD only)
const WHATSAPP_NUMBER = '+393488148192';
const WHATSAPP_MESSAGE = 'Ciao Elia, vorrei rinnovare il mio abbonamento';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const SubscriptionBanner = () => {
  const { activeModule, isImpersonating } = useModule();
  const { isAdmin, isAuthenticated, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  
  // Only show for non-admin users in Arbicsx CFD module, not when impersonating
  const shouldCheck = isAuthenticated && !isAdmin && !isImpersonating && activeModule === MODULES.ARBICSX_CFD;
  
  useEffect(() => {
    if (!shouldCheck) {
      setSubscription(null);
      return;
    }
    
    const fetchSubscription = async () => {
      try {
        const res = await getMySubscription();
        setSubscription(res.data);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchSubscription();
    // Refresh every 5 minutes
    const interval = setInterval(fetchSubscription, 300000);
    return () => clearInterval(interval);
  }, [shouldCheck, user?.id]);
  
  if (!subscription || subscription.is_free || subscription.is_blocked) return null;
  
  const { status, days_left, color } = subscription;
  
  // Expired: alarm (red) compact bar - positioned over main content only, not sidebar
  if (status === 'scaduto') {
    return (
      <div 
        className="fixed top-0 right-0 left-0 lg:left-[240px] z-[45] bg-[#0d1117] border-b border-[rgba(248,113,113,.45)] rounded-b-[14px] py-1.5 px-3" 
        data-testid="subscription-expired-banner"
      >
        <div className="flex items-center justify-center gap-2 text-xs sm:text-sm">
          <AlertCircle size={14} className="ht-alert flex-shrink-0" />
          <span className="font-medium truncate ht-alert">Abbonamento scaduto</span>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="ht-btn ht-btn-sm ht-btn-danger flex-shrink-0"
            data-testid="subscription-expired-renew-btn"
          >
            <ExternalLink size={10} />
            Rinnova
          </a>
        </div>
      </div>
    );
  }
  
  // Expiring warning bar - yellow, red only when it expires today
  if (status === 'in_scadenza' && days_left !== undefined) {
    const isToday = days_left === 0;
    const accentClass = isToday ? 'ht-alert' : 'ht-warn';
    const borderClass = isToday ? 'border-[rgba(248,113,113,.45)]' : 'border-[rgba(240,180,90,.45)]';
    const message = isToday ? 'Scade oggi' : `Scade tra ${days_left}gg`;
    
    return (
      <div 
        className={`fixed top-0 right-0 left-0 lg:left-[240px] z-[45] bg-[#0d1117] border-b ${borderClass} rounded-b-[14px] py-1.5 px-3`} 
        data-testid="subscription-warning-banner"
      >
        <div className="flex items-center justify-center gap-2 text-xs sm:text-sm">
          <Clock size={14} className={`${accentClass} flex-shrink-0`} />
          <span className={`font-medium ${accentClass}`}>{message}</span>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`ht-btn ht-btn-sm ${isToday ? 'ht-btn-danger' : 'ht-btn-warn'} flex-shrink-0`}
            data-testid="subscription-warning-renew-btn"
          >
            <ExternalLink size={10} />
            Rinnova
          </a>
        </div>
      </div>
    );
  }
  
  return null;
};

// Maintenance/Lock Screen Component
const MaintenanceScreen = ({ type }) => {
  const { logout } = useAuth();
  const locked = type === 'locked';
  
  return (
    <div className="fixed inset-0 bg-black z-[200] flex items-center justify-center p-4">
      <AmbientNetwork />
      <div className="max-w-md w-full text-center space-y-8 relative z-10">
        {/* Logo */}
        <div className="flex justify-center" data-net-exclude>
          <img 
            src="/icons/ht-logo-v3.png" 
            alt="Hedging Tracker" 
            className="w-20 h-20 rounded-[18px] object-contain ht-logo"
          />
        </div>

        {/* Icon */}
        <div className="flex justify-center" data-net-exclude>
          <div
            className="w-20 h-20 rounded-full border flex items-center justify-center"
            style={{ borderColor: locked ? 'rgba(248, 113, 113, 0.45)' : 'rgba(240, 180, 90, 0.45)' }}
          >
            {locked
              ? <Lock size={36} className="ht-alert" />
              : <Wrench size={36} className="ht-warn" />}
          </div>
        </div>

        {/* Message */}
        <div className="space-y-4" data-net-exclude>
          <h1 className="text-2xl font-bold text-white font-heading">
            {locked ? 'App Bloccata' : 'App in Manutenzione'}
          </h1>
          <p className="text-gray-400">
            {locked
              ? "L'accesso all'app è stato temporaneamente sospeso. Riprova più tardi."
              : 'Stiamo effettuando aggiornamenti. Torna tra qualche minuto.'}
          </p>
        </div>

        {/* Logout Button */}
        <div className="flex justify-center" data-net-exclude>
          <button
            type="button"
            onClick={logout}
            className="ht-btn"
            data-testid="maintenance-logout-btn"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { moduleLabel, isImpersonating, activeModule, isAdminSecondary } = useModule();
  const { user, isAdmin, isAuthenticated, refreshUser } = useAuth();
  const [appStatus, setAppStatus] = useState({ app_locked: false, maintenance_mode: false });
  const [subscription, setSubscription] = useState(null);
  const [showNominationModal, setShowNominationModal] = useState(false);
  // Colore del gruppo dell'admin se presente, altrimenti azzurro (solo aspetto)
  const nominationColor = user?.group_color || '#4d8ff7';
  
  // Check if subscription banner should be shown
  const shouldCheckSubscription = isAuthenticated && !isAdmin && !isImpersonating && activeModule === MODULES.ARBICSX_CFD;
  const showSubscriptionBanner = subscription && !subscription.is_free && !subscription.is_blocked && 
    (subscription.status === 'scaduto' || subscription.status === 'in_scadenza');
  
  // Check for nomination pending (admin secondario appena nominato)
  useEffect(() => {
    if (user?.nomination_pending && isAdminSecondary) {
      setShowNominationModal(true);
    }
  }, [user?.nomination_pending, isAdminSecondary]);

  // Alone sul bordo delle schede: un solo listener globale
  useEffect(() => startCardGlow(), []);

  // Effetto magnetico dei tasti [data-magnetic]: un solo listener globale
  useEffect(() => startMagnetic(), []);
  
  // Clear nomination pending when modal is closed
  const handleCloseNominationModal = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/admin/clear-nomination-pending`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setShowNominationModal(false);
      // Refresh user data to update the UI
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error clearing nomination:', error);
      setShowNominationModal(false);
    }
  };
  
  // Check app status periodically
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { getPublicAppStatus } = await import('../lib/api');
        const res = await getPublicAppStatus();
        setAppStatus(res.data);
      } catch (error) {
        // Silently fail
      }
    };
    
    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  // Fetch subscription status for non-admin users in Arbicsx CFD
  useEffect(() => {
    if (!shouldCheckSubscription) {
      setSubscription(null);
      return;
    }
    
    const fetchSubscription = async () => {
      try {
        const res = await getMySubscription();
        setSubscription(res.data);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchSubscription();
    const interval = setInterval(fetchSubscription, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [shouldCheckSubscription, user?.id]);
  
  // Show maintenance/lock screen for non-admin users
  if (isAuthenticated && !isAdmin) {
    if (appStatus.app_locked) {
      return <MaintenanceScreen type="locked" />;
    }
    if (appStatus.maintenance_mode) {
      return <MaintenanceScreen type="maintenance" />;
    }
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Sfondo animato di contorno: montato una volta per tutta l'area (zone di rispetto automatiche) */}
      <AmbientNetwork auto />

      {/* Notification Popup (Arbicsx CFD only) */}
      <NotificationPopup />
      
      {/* Subscription Expiry Banner (Arbicsx CFD only) */}
      <SubscriptionBanner />
      
      {/* Admin Message Popup */}
      <AdminMessagePopup />
      
      {/* Impersonation Banner */}
      <ImpersonationBanner />
      
      {/* Mobile Header - adjust top position if subscription banner is shown */}
      <header className={`lg:hidden fixed left-0 right-0 h-14 bg-[#0d1117] border-b border-[#282c34] flex items-center justify-between px-4 z-30 ${showSubscriptionBanner ? 'top-7' : 'top-0'}`}>
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-[#11161d] transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
        <div className="flex items-center gap-2">
          <img 
            src="/icons/ht-logo-v3.png" 
            alt="HT" 
            className="w-9 h-9 rounded-[9px] object-contain ht-logo ht-logo-sm"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.style.display = 'none';
            }}
          />
          <div className="flex flex-col">
            <span className="font-bold text-white font-heading text-sm">Hedging Tracker</span>
            <span className="text-[10px] ht-pos">{moduleLabel}</span>
          </div>
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <UpdateBanner />
      
      <main className={`
        min-h-screen
        ${showSubscriptionBanner ? 'pt-[84px] lg:pt-7' : isImpersonating ? 'pt-24 lg:pt-10' : 'pt-14 lg:pt-0'}
        lg:ml-[240px]
      `}>
        <div key={location.pathname} className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto page-fade">
          {children}
        </div>
      </main>
      
      {/* Nomination Modal - Shows when user is newly nominated as admin_secondary */}
      {showNominationModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="ht-panel p-6 sm:p-8 max-w-md w-full" data-testid="nomination-modal">
            <div className="text-center">
              <div
                className="w-16 h-16 mx-auto mb-6 rounded-full border flex items-center justify-center"
                style={{ borderColor: nominationColor, background: `${nominationColor}14` }}
              >
                <ShieldCheck className="w-7 h-7" style={{ color: nominationColor }} />
              </div>
              <h2 className="text-xl font-bold text-[#e6e9ee] mb-3 font-heading">
                Congratulazioni
              </h2>
              <p className="text-base text-[#e6e9ee] mb-4">
                Sei stato nominato come <span className="font-semibold" style={{ color: nominationColor }}>Amministratore</span>, 
                ora puoi creare la tua rete di utenti
              </p>
              <p className="text-sm text-[#8b919c] mb-7">
                Nella sezione Utenti troverai il tuo link di invito personale da condividere.
              </p>
              <button
                onClick={handleCloseNominationModal}
                className="ht-btn ht-btn-primary w-full justify-center"
                data-testid="nomination-modal-ack-btn"
              >
                Inizia a costruire la tua rete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
